import mergeWith from 'lodash/mergeWith'
import type { Flow, FlowStyles } from '../models/types'
import formatSelector from './formatSelector'
import replaceStorageURLsFromFirebaseToCloudflare from '../helpers/replaceStorageURLsFromFirebaseToCloudflare'
import { getBaseBuilderVersion } from '../helpers/utils'

export default function stylesGenerator(flow: Flow, flowId: string, popup: boolean): string {
  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  const defaultStylesObj = defaultStyles(builderVersion, popup, flow)

  //* For builder version 4, we enforce an ordering of styles by breakpoint of json styles
  let jsonStyles = builderVersion > 3 ? orderJsonStyles(flow.styles || {}) : flow.styles || {}

  const mergedStyles: FlowStyles = mergeWith(defaultStylesObj, jsonStyles)

  const css = Object.entries(mergedStyles)
    .map((selector) => {
      const isHoverSelector = selector.includes(':hover')
      const selectorContent = `${formatSelector(selector[0], flowId)} {\n ${Object.entries(
        selector[1] || {}
      )
        .map(
          (style) =>
            `${style[0]}: ${
              typeof style[1] === 'string'
                ? replaceStorageURLsFromFirebaseToCloudflare(style[1])
                : style[1]
            };\n`
        )
        .join('\n')}\n } \n`
      /* Disable hover on mobile because it procs on a "tap" */
      return isHoverSelector ? `@media (hover:hover) { ${selectorContent} }` : selectorContent
    })
    .join('\n')

  // return css + '\n' + getAnimations(flow) + '\n\n' + getMediaQueries() + '\n'
  return css + getAnimations(flow)
}

function defaultStyles(builderVersion: number, popup: boolean, flow: Flow): FlowStyles {
  const popupStyles = popup ? generatePopupStyles() : {}

  switch (builderVersion) {
    case 3:
      return {
        // '.Flow-EntireFlow[data-controller*="flow-edit"][data-flow-edit-is-editing-flow-value="false"]:hover':
        //   {
        //     cursor: 'pointer',
        //     outline: '2px dashed #999',
        //   },
        // '.Flow-EntireFlow[data-controller*="flow-edit"][data-flow-edit-is-editing-flow-value="false"] *':
        //   {
        //     'pointer-events': 'none',
        //   },
        // '.Flow-EntireFlow[data-controller*="flow-edit"][data-flow-edit-is-editing-flow-value="false"] [data-flow-edit-is-editing-flow-value="true"] *':
        //   {
        //     'pointer-events': 'auto',
        //   },
        // '.Flow-EntireFlow[data-controller*="flow-edit"][data-flow-edit-is-editing-flow-value="true"]':
        //   {
        //     outline: '1px dashed #555',
        //   },
        // '[contenteditable-suppressed="true"]': {
        //   cursor: 'default',
        // },
        // '[data-component-type-value="CustomButton"] [contenteditable-suppressed="true"]': {
        //   cursor: 'pointer',
        // },
        // '[data-component-type-value="OptionSelector"] [contenteditable-suppressed="true"]': {
        //   cursor: 'pointer',
        // },
        // '[contenteditable="true"]': {
        //   outline: 'none',
        // },
        // '[data-component-type-value="PlainText"][data-editing-mode-component-selected="true"] [contenteditable="true"], [data-component-type-value="CustomButton"][data-editing-mode-component-selected="true"] [contenteditable="true"], [data-component-type-value="OptionSelector"][data-editing-mode-component-selected="true"] [contenteditable="true"], [data-component-type-value="InputBox"][data-editing-mode-component-selected="true"] [contenteditable="true"]':
        //   {
        //     cursor: 'text !important',
        //     'pointer-events': 'auto !important',
        //   },
        // '[data-component-type-value="PlainText"][data-editing-mode-component-selected="true"] [contenteditable="true"]:hover, [data-component-type-value="CustomButton"][data-editing-mode-component-selected="true"] [contenteditable="true"]:hover, [data-component-type-value="OptionSelector"][data-editing-mode-component-selected="true"] [contenteditable="true"]:hover, [data-component-type-value="InputBox"][data-editing-mode-component-selected="true"] [contenteditable="true"]:hover':
        //   {
        //     outline: '1px dashed #aaa !important',
        //   },
        // '[data-edit-editing-value="true"]': {
        //   outline: '1px dashed #aaa',
        //   // outline: 'none',
        // },
        '.loading': {
          opacity: '0.5 !important',
        },
        '.RichTextMarkdown .ql-container': {
          'pointer-events': 'none',
          'font-size': 'inherit',
          'font-family': 'inherit',
          'font-weight': 'inherit',
          'line-height': 'inherit',
        },
        '.RichTextMarkdown .ql-container a': {
          'pointer-events': 'auto',
        },
        '.RichTextMarkdown[data-editing-mode-component-selected=true] .ql-container': {
          'z-index': '2000500',
          'pointer-events': 'auto',
        },
        '.RichTextMarkdown :not([class*="ql"]) p': {
          'margin-block-start': '0px',
          'margin-block-end': '0px',
        },
        '.RichTextMarkdown .ql-editor': {
          padding: '0px',
          'line-height': 'inherit',
          'tab-size': 'inherit',
          'text-align': 'inherit',
          'white-space': 'inherit',
          'word-wrap': 'inherit',
          overflow: 'hidden',
        },
        '.RichTextMarkdown .ql-tooltip': {
          'min-width': '366px',
          height: '40px',
          'line-height': '1.5',
          'font-size': '13px',
          'font-family': 'Work Sans',
        },
        // Any ql-tooltip inside a component not selected should be hidden
        ':not[data-editing-mode-component-selected=true].RichTextMarkdown .ql-tooltip': {
          display: 'none',
        },
        '.Flow-EntireFlow *': { 'box-sizing': 'border-box' },
        // ...generateStripeStyles(),
        ...getNormalizedStyles(builderVersion),
        ...getResetStyles(flow.reset_css),
        ...generateFileUploadStyles(),
        ...generateToggleSwitchStyles(),
        ...generateSliderStyles(),
        ...generateInfoBoxStyles(),
        ...popupStyles,
        ...generateProgressBarStyles(),
        ...generateOptionSelectorDropdownStyles(),
        ...generateBookMeetingStyles(),
        '.Flow-Element.ElementType-OfflineIndicator': {
          position: 'absolute',
          bottom: '0',
          'z-index': '50',
          width: '100%',
          padding: '10px',
          'text-align': 'center',
          'background-color': 'lightcoral',
          color: 'white',
          'font-weight': '400',
        },
        '.Flow-EntireFlow': {
          '--primary-color': '#6454f3',
          '--fail-color': 'red',
          '--border-radius': '4px',
          position: 'relative',

          // Reset any inherited styles??
          'text-align': 'left',
          'box-sizing': 'border-box',

          // Set default styles
          'font-family': "'Lato', sans-serif",
          'font-size': '16px',
          'font-weight': '300',
          'line-height': '1.5',
          color: '#555',
        },
        // '.Flow-EntireFlow a.ComponentType-CustomButton': { display: 'block' },
        '.Flow-Page': {
          position: 'relative',
          padding: '32px',
          'box-sizing': 'border-box',
        },
        '.ElementType-PageContents': {
          width: '100%',
        },
        // Component Types
        '.Flow-Component.ComponentType-RichText p': {
          margin: '0',
        },
        '.Flow-Component.ComponentType-RichText ul': {
          'list-style': 'disc',
          'padding-left': '1.1em',
        },
        '.Flow-Component.ComponentType-InputBox': {
          display: 'flex',
          'flex-direction': 'column',
          position: 'relative',
          'max-width': '100%',
          width: '300px',
        },
        '.Flow-Component.ComponentType-OptionSelector': {
          display: 'flex',
          'flex-direction': 'column',
          position: 'relative',
        },
        '.Flow-Component.ComponentType-CustomButton': {
          'box-sizing': 'border-box',
          padding: '8px 32px',
          'border-radius': 'var(--border-radius)',
          'background-color': 'white',
          color: 'inherit',
          transition: 'background-color 0.3s, color 0.3s',
          cursor: 'pointer',
        },
        '.Flow-Component.CustomButton_link': {
          'text-decoration': 'none',
          display: 'flex',
          'flex-direction': 'row',
        },
        '.Flow-Component.ComponentType-MediaEmbed': {
          position: 'relative',
          width: '100%',
        },

        // Component States
        '.Flow-Component.ComponentType-CustomButton.selected': {
          'border-color': 'var(--primary-color)',
        },
        '.Flow-Component.ComponentType-CustomButton.disabled': {
          cursor: 'default',
          background: '#eee',
          'pointer-events': 'none',
          color: '#aaa',
        },

        // Conponent Tags
        '.Flow-Component.ComponentTag-main_container': {
          display: 'flex',
          'flex-direction': 'column',
          'justify-content': 'flex-start',
          'align-items': 'center',
        },
        '.Flow-Component.ComponentTag-row': {
          flex: 1,
          display: 'flex',
          'flex-direction': 'row',
          'justify-content': 'stretch',
        },
        '.Flow-Component.ComponentTag-column': {
          flex: 1,
          display: 'flex',
          'flex-direction': 'column',
        },
        '.Flow-Component.ComponentTag-title': {
          'font-size': '44px',
          'font-weight': '600',
          'justify-content': 'center',
          display: 'flex',
          'margin-bottom': '16px',
        },
        '.Flow-Component.ComponentTag-subtitle': {
          'justify-content': 'center',
          display: 'flex',
          'font-size': '24px',
          'font-weight': '300',
          'margin-bottom': '16px',
        },
        '.Flow-Component.ComponentTag-dropdown_selector': {
          display: 'flex',
          'margin-top': '20px',
        },
        '.Flow-Component.ComponentTag-standard_input': {
          display: 'flex',
        },
        '.Flow-Component.ComponentTag-checkbox': {
          display: 'flex',
        },
        '.Flow-Component.ComponentTag-buttons_wrapped': {
          display: 'flex',
        },
        '.Flow-Component.ComponentTag-buttons_bars': {
          display: 'flex',
          'flex-direction': 'column',
          width: '100%',
          'align-items': 'center',
        },
        '.Flow-Component.ComponentTag-range': {},
        '.Flow-Component.ComponentTag-stripe_checkout': {},
        '.Flow-Component.ComponentTag-image': {},
        '.Flow-Component.ComponentTag-inputs_container': {
          display: 'flex',
          'flex-wrap': 'wrap',
          'justify-content': 'center',
        },
        '.Flow-Component.ComponentTag-header': {
          'justify-content': 'flex-end',
          display: 'flex',
          'flex-direction': 'row',
        },
        '.Flow-Component.ComponentTag-footer': {
          display: 'flex',
          'flex-direction': 'row',
          'justify-content': 'space-between',
          'align-items': 'center',
        },
        // '.Flow-Component.ComponentTag-global_next': {
        //   'justify-content': 'center',
        //   'align-items': 'center',
        //   display: 'flex',
        // },
        '.Flow-Component.ComponentTag-global_prev': {
          'background-color': 'transparent',
          border: 'none',
          'padding-left': '0px',
          'padding-right': '0px',
        },

        // Component Elements
        '.Flow-Element.ElementType-Label': {
          display: 'flex',
          'flex-direction': 'row',
          'align-items': 'end',
        },
        '.Flow-Element.ElementType-InputElement': {
          padding: '8px 16px',
          'border-radius': 'var(--border-radius)',
          border: '1px solid #ddd',
          width: '100%',
        },
        '.Flow-Element.ElementType-InputTextarea': {
          padding: '8px 16px',
          'border-radius': 'var(--border-radius)',
          border: '1px solid #ddd',
          width: '100%',
        },
        '.Flow-Element.ElementType-Image': {
          'max-width': '100%',
        },
        '.Flow-Element.ElementType-OptionButtonList': {
          display: 'flex',
          // 'flex-direction': 'row',
          'align-items': 'stretch',
          'justify-content': 'center',
          'flex-wrap': 'wrap',
        },
        '.Flow-Element.ElementType-OptionButtonCard': {
          display: 'flex',
          'flex-direction': 'column',
          'align-items': 'center',
          'justify-content': 'space-between',
          padding: '8px 32px',
          margin: '8px',
          flex: '0 0 auto',
          'text-align': 'center',
          'box-sizing': 'border-box',
          border: '1px solid #ddd',
          'background-color': 'white',
          'border-radius': 'var(--border-radius)',
          cursor: 'pointer',
          'line-height': '1.3',
          transition: 'transform 0.1s, border-color 0.5s, background-color 0.2s',
        },
        '.Flow-Element.ElementType-ButtonIconImage': {
          'max-width': '100%',
          'max-height': '50px',
        },
        '.Flow-Element.ElementType-ButtonIcon': {
          'font-size': '40px',
          color: '#999',
        },

        // States
        '.Flow-Component:selected .ElementType-ButtonIcon': {
          color: 'var(--primary-color)',
        },

        // Component Tags - Element
        '.Flow-Element.ElementType-OptionButtonList.ComponentTag-buttons_bars': {
          width: '100%',
          'max-width': '420px',
          'flex-direction': 'column',
          'flex-wrap': 'nowrap',
        },
        '.Flow-Element.ElementType-OptionButtonCard.ComponentTag-buttons_tiles': {
          padding: '30px 15px',
          width: '140px',
          'max-width': '140px',
          'min-height': '150px',
          'border-radius': 'var(--border-radius-lg)',
        },
        '.Flow-Element.ElementType-OptionButtonCard.ComponentTag-buttons_wrapped': {
          'max-width': '100%',
          'white-space': 'nowrap',
        },
        '.Flow-Element.ElementType-OptionButtonCard.ComponentTag-buttons_bars': {
          width: '100%',
          'max-width': '420px',
        },
        '.Flow-Element.ElementType-OptionButtonCardIcon.ComponentTag-buttons_wrapped': {
          display: 'none',
        },
        '.Flow-Element.ElementType-StripeCheckoutButton': {
          padding: '10px 20px',
          background: 'var(--primary-color)',
          color: 'white',
          'border-radius': '6px',
          border: 'none',
          cursor: 'pointer',
          transition: 'padding 0.3s',
        },
        '.Flow-Element.ElementType-StripeCheckoutButton[disabled]': {
          cursor: 'default',
        },
        '.Flow-Element.ElementType-StripeCheckoutButton .Icon': {
          'font-size': '0.8em',
          margin: '0 0 0 0.375em',
        },
        '.ep-circle': {
          'transform-origin': '50% 50%',
        },
        'g.ep-circle--container': {
          transition: 'inherit',
          'transform-origin': '50% 50%',
        },

        // UI Kits added by Lucian
        '.Flow-Component.ComponentType-ChatMessages': {
          width: '90%',
          'max-width': '700px',
          'max-height': '80vh',
          'margin-top': '20px',
          'margin-bottom': '20px',
          'border-radius': '8px',
          'background-color': 'rgb(39, 39, 42)',
          padding: '20px',
          border: '1px solid #ddd',
          'margin-left': 'auto',
          'margin-right': 'auto',
          'box-shadow': '0 0 10px rgba(0,0,0,0.1)',
          color: '#F2F2F2',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-ChatMessageText': {
          'border-top': '1px solid #fafafa38',
          'padding-bottom': '20px',
          'padding-top': '20px',
          width: '100%',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-ChatMessageContainer': {
          display: 'flex',
          'flex-direction': 'row',
          'justify-content': 'flex-start',
          'align-items': 'center',
          gap: '20px',
          width: '100%',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-ChatMessageSvgClasses': {
          'margin-top': '15px',
          'margin-bottom': '10px',
          width: '32px',
          height: '32px',
          'min-width': '32px',
          'min-height': '32px',
          'max-width': '32px',
          'max-height': '32px',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-ChatMessageShowHideButtonClasses': {
          'margin-right': 'auto',
          'margin-left': 'auto',
          'margin-top': '20px',
          'margin-bottom': '20px',
          'background-color': 'transparent',
          border: 'none',
          color: 'black',
          cursor: 'pointer',
          width: '100%',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-ChatMessageAiLeftContainerClasses': {
          display: 'flex',
          'flex-direction': 'column',
          'justify-content': 'flex-start',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-ChatMessageFeedbackButton': {
          'background-color': 'transparent',
          border: 'none',
          padding: '0px',
          'margin-top': '0px',
          cursor: 'pointer',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-InputContainer': {
          display: 'flex',
          'flex-direction': 'row',
          gap: '20px',
          'justify-content': 'center',
          'align-items': 'center',
          border: '1px solid #fafafa38',
          'border-radius': '15px',
          'background-color': 'black',
          'margin-top': '20px',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-Input': {
          padding: '20px',
          'justify-content': 'center',
          'background-color': 'black',
          color: '#fafafa',
          width: '100%',
          'border-color': 'transparent',
          'border-radius': '15px',
          outline: 'none',
          resize: 'none',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-Button': {
          padding: '8px 16px',
          'background-color': 'black',
          color: '#fafafa',
          border: '1px solid #fafafa38',
          'border-radius': '5px',
          'margin-right': '20px',
          height: 'fit-content',
          cursor: 'pointer',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-MessagesContainer': {
          'max-height': 'calc(80vh - 150px)',
          'overflow-y': 'auto',
          // 'padding-right': '20px',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-ChatMessageAvatar': {
          width: '32px',
          height: '32px',
          'min-width': '32px',
          'min-height': '32px',
          'max-width': '32px',
          'max-height': '32px',
          'border-radius': '50%',
          overflow: 'hidden',
          'margin-bottom': '10px',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-MessagesContainer::-webkit-scrollbar':
          {
            width: '0px',
          },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-ChatMessageImageURLClasses': {
          width: '100%',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-Input::-webkit-scrollbar': {
          width: '0px',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-QuickReplyOptionButton': {
          color: 'white',
          'background-color': 'black',
          'padding-left': '10px',
          'padding-right': '10px',
          'padding-top': '5px',
          'padding-bottom': '5px',
          'border-radius': '5px',
          border: '1px solid #fafafa38',
          cursor: 'pointer',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-QuickReplyOptionsContainer': {
          'padding-left': '50px',
          width: '100%',
          display: 'flex',
          'flex-direction': 'row',
          'justify-content': 'flex-start',
          'flex-wrap': 'wrap',
          gap: '10px',
          'margin-top': '-10px',
          'margin-bottom': '10px',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-QuickInitialOptions': {
          color: 'black',
          'background-color': '#00FFA3',
          'padding-left': '10px',
          'padding-right': '10px',
          'padding-top': '15px',
          'padding-bottom': '15px',
          'border-radius': '5px',
          border: '1px solid #fafafa38',
          cursor: 'pointer',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementTag-QuickInitialOptionsContainer': {
          'padding-top': '30px',
          'padding-bottom': '20px',
          'padding-left': '10px',
          'padding-right': '10px',
          width: '100%',
          display: 'flex',
          'flex-direction': 'column',
          'justify-content': 'flex-start',
          gap: '15px',
          'margin-top': '-10px',
          'margin-bottom': '10px',
        },
        '.Flow-Element.ComponentType-ChatMessages.ElementType-ChatMessageLoading': {
          width: '50px',
          'min-width': '50px',
          'max-width': '50px',
          'margin-right': '18px',
        },
      }

    case 4:
      return {
        ...getNormalizedStyles(builderVersion),
        //* Styles for flows in edit mode (old flow editor) - To remove when new flow editor is fully deprecated -------------------------
        '.Flow-EntireFlow[data-controller*="flow-edit"][data-flow-edit-is-editing-flow-value="false"]:hover':
          {
            cursor: 'pointer',
            outline: '2px dashed #999',
          },
        '.Flow-EntireFlow[data-controller*="flow-edit"][data-flow-edit-is-editing-flow-value="false"] *':
          {
            'pointer-events': 'none',
          },
        '.Flow-EntireFlow[data-controller*="flow-edit"][data-flow-edit-is-editing-flow-value="false"] [data-flow-edit-is-editing-flow-value="true"] *':
          {
            'pointer-events': 'auto',
          },
        '.Flow-EntireFlow[data-controller*="flow-edit"][data-flow-edit-is-editing-flow-value="true"]':
          {
            outline: '1px dashed #555',
          },
        '[contenteditable-suppressed="true"]': {
          cursor: 'default',
        },
        '[data-component-type-value="CustomButton"] [contenteditable-suppressed="true"]': {
          cursor: 'pointer',
        },
        '[data-component-type-value="OptionSelector"] [contenteditable-suppressed="true"]': {
          cursor: 'pointer',
        },
        '[contenteditable="true"]': {
          outline: 'none',
        },
        '[data-component-type-value="PlainText"][data-editing-mode-component-selected="true"] [contenteditable="true"], [data-component-type-value="CustomButton"][data-editing-mode-component-selected="true"] [contenteditable="true"], [data-component-type-value="OptionSelector"][data-editing-mode-component-selected="true"] [contenteditable="true"], [data-component-type-value="InputBox"][data-editing-mode-component-selected="true"] [contenteditable="true"]':
          {
            cursor: 'text !important',
            'pointer-events': 'auto !important',
          },
        '[data-component-type-value="PlainText"][data-editing-mode-component-selected="true"] [contenteditable="true"]:hover, [data-component-type-value="CustomButton"][data-editing-mode-component-selected="true"] [contenteditable="true"]:hover, [data-component-type-value="OptionSelector"][data-editing-mode-component-selected="true"] [contenteditable="true"]:hover, [data-component-type-value="InputBox"][data-editing-mode-component-selected="true"] [contenteditable="true"]:hover':
          {
            outline: '1px dashed #aaa !important',
          },
        '[data-edit-editing-value="true"]': {
          outline: '1px dashed #aaa',
        },
        '.loading': {
          opacity: '0.5 !important',
        },
        '.RichTextMarkdown .ql-container': {
          'pointer-events': 'none',
          'font-size': 'inherit',
          'font-family': 'inherit',
          'font-weight': 'inherit',
          'line-height': 'inherit',
        },
        '.RichTextMarkdown .ql-container a': {
          'pointer-events': 'auto',
        },
        '.RichTextMarkdown[data-editing-mode-component-selected=true] .ql-container': {
          'z-index': '2000500',
          'pointer-events': 'auto',
        },
        '.RichTextMarkdown :not([class*="ql"]) p': {
          'margin-block-start': '0px',
          'margin-block-end': '0px',
        },
        '.RichTextMarkdown .ql-editor': {
          padding: '0px',
          'line-height': 'inherit',
          'tab-size': 'inherit',
          'text-align': 'inherit',
          'white-space': 'inherit',
          'word-wrap': 'inherit',
          overflow: 'hidden',
        },
        '.RichTextMarkdown .ql-tooltip': {
          'min-width': '366px',
          height: '40px',
          'line-height': '1.5',
          'font-size': '13px',
          'font-family': 'Work Sans',
        },
        ':not[data-editing-mode-component-selected=true].RichTextMarkdown .ql-tooltip': {
          display: 'none',
        },

        //* Set everything to use border-box
        '.Flow-EntireFlow *': { 'box-sizing': 'border-box' },

        //* Handlers of InfoBox
        '.InfoBox:not(.showing)': {
          'pointer-events': 'none',
        },
        '.InfoBox:not(.showing) .InfoBox-background': {
          opacity: 0,
        },
        '.InfoBox:not(.showing) .InfoBox-box': {
          bottom: '-100vh',
        },
        //* Hide native dialog backdrop so only the styled .InfoBox-background div is visible.
        '.InfoBox dialog.InfoBox-box::backdrop': {
          display: 'none',
        },

        //* Advanced dropdown hidden select
        '.Flow-Element.ElementType-OptionDropdown.ComponentTag-standard_dropdown .ElementType-DropdownMain select':
          {
            '-webkit-appearance': 'none',
            '-moz-appearance': 'none',
            appearance: 'none',
            'font-family': 'inherit',
            'font-size': 'inherit',
            padding: '0',
            'border-width': '0',
            width: '100%',
            flex: '1',
            display: 'none',
          },
      }
    default:
      return {}
  }
}

function getResetStyles(reset_css: boolean | undefined): FlowStyles | undefined {
  if (!reset_css) {
    return
  }

  return {
    '.Flow-EntireFlow a:link': {
      color: '(internal value)',
      'text-decoration': 'underline',
      cursor: 'auto',
    },
    '.Flow-EntireFlow a:visited': {
      color: '(internal value)',
      'text-decoration': 'underline',
      cursor: 'auto',
    },
    '.Flow-EntireFlow a:link:active ': {
      color: '(internal value)',
    },
    '.Flow-EntireFlow a:visited:active ': {
      color: '(internal value)',
    },
    '.Flow-EntireFlow address ': {
      display: 'block',
      'font-style': 'italic',
    },
    '.Flow-EntireFlow area ': {
      display: 'none',
    },
    '.Flow-EntireFlow article ': {
      display: 'block',
    },
    '.Flow-EntireFlow aside ': {
      display: 'block',
    },
    '.Flow-EntireFlow b ': {
      'font-weight': 'bold',
    },
    '.Flow-EntireFlow bdo ': {
      'unicode-bidi': 'bidi-override',
    },
    '.Flow-EntireFlow blockquote ': {
      display: 'block',
      'margin-top': '1em',
      'margin-bottom': '1em',
      'margin-left': '40px',
      'margin-right': '40px',
    },
    '.Flow-EntireFlow caption ': {
      display: 'table-caption',
      'text-align': 'center',
    },
    '.Flow-EntireFlow cite ': {
      'font-style': 'italic',
    },
    '.Flow-EntireFlow code ': {
      'font-family': 'monospace',
    },
    '.Flow-EntireFlow col ': {
      display: 'table-column',
    },
    '.Flow-EntireFlow colgroup ': {
      display: 'table-column-group',
    },
    '.Flow-EntireFlow datalist ': {
      display: 'none',
    },
    '.Flow-EntireFlow dd ': {
      display: 'block',
      'margin-left': '40px',
    },
    '.Flow-EntireFlow del ': {
      'text-decoration': 'line-through',
    },
    '.Flow-EntireFlow details ': {
      display: 'block',
    },
    '.Flow-EntireFlow dfn ': {
      'font-style': 'italic',
    },
    '.Flow-EntireFlow div ': {
      display: 'block',
    },
    '.Flow-EntireFlow dl ': {
      display: 'block',
      'margin-top': '1em',
      'margin-bottom': '1em',
      'margin-left': '0',
      'margin-right': '0',
    },
    '.Flow-EntireFlow dt ': {
      display: 'block',
    },
    '.Flow-EntireFlow em ': {
      'font-style': 'italic',
    },
    '.Flow-EntireFlow embed:focus ': {
      outline: 'none',
    },
    '.Flow-EntireFlow fieldset ': {
      display: 'block',
      'margin-left': '2px',
      'margin-right': '2px',
      'padding-top': '0.35em',
      'padding-bottom': '0.625em',
      'padding-left': '0.75em',
      'padding-right': '0.75em',
      border: '2px groove (internal value)',
    },
    '.Flow-EntireFlow figcaption ': {
      display: 'block',
    },
    '.Flow-EntireFlow figure ': {
      display: 'block',
      'margin-top': '1em',
      'margin-bottom': '1em',
      'margin-left': '40px',
      'margin-right': '40px',
    },
    '.Flow-EntireFlow footer ': {
      display: 'block',
    },
    '.Flow-EntireFlow form ': {
      display: 'block',
      'margin-top': '0em',
    },
    '.Flow-EntireFlow h1 ': {
      display: 'block',
      'font-size': '2em',
      'margin-top': '0.67em',
      'margin-bottom': '0.67em',
      'margin-left': '0',
      'margin-right': '0',
      'font-weight': 'bold',
    },
    '.Flow-EntireFlow h2 ': {
      display: 'block',
      'font-size': '1.5em',
      'margin-top': '0.83em',
      'margin-bottom': '0.83em',
      'margin-left': '0',
      'margin-right': '0',
      'font-weight': 'bold',
    },
    '.Flow-EntireFlow h3 ': {
      display: 'block',
      'font-size': '1.17em',
      'margin-top': '1em',
      'margin-bottom': '1em',
      'margin-left': '0',
      'margin-right': '0',
      'font-weight': 'bold',
    },
    '.Flow-EntireFlow h4 ': {
      display: 'block',
      'margin-top': '1.33em',
      'margin-bottom': '1.33em',
      'margin-left': '0',
      'margin-right': '0',
      'font-weight': 'bold',
    },
    '.Flow-EntireFlow h5 ': {
      display: 'block',
      'font-size': '0.83em',
      'margin-top': '1.67em',
      'margin-bottom': '1.67em',
      'margin-left': '0',
      'margin-right': '0',
      'font-weight': 'bold',
    },
    '.Flow-EntireFlow h6 ': {
      display: 'block',
      'font-size': '0.67em',
      'margin-top': '2.33em',
      'margin-bottom': '2.33em',
      'margin-left': '0',
      'margin-right': '0',
      'font-weight': 'bold',
    },
    '.Flow-EntireFlow head ': {
      display: 'none',
    },
    '.Flow-EntireFlow header ': {
      display: 'block',
    },
    '.Flow-EntireFlow hr ': {
      display: 'block',
      'margin-top': '0.5em',
      'margin-bottom': '0.5em',
      'margin-left': 'auto',
      'margin-right': 'auto',
      'border-style': 'inset',
      'border-width': '1px',
    },
    '.Flow-EntireFlow html ': {
      display: 'block',
    },
    '.Flow-EntireFlow html:focus ': {
      outline: 'none',
    },
    '.Flow-EntireFlow i ': {
      'font-style': 'italic',
    },
    '.Flow-EntireFlow iframe:focus ': {
      outline: 'none',
    },
    '.Flow-EntireFlow iframe[seamless] ': {
      display: 'block',
    },
    '.Flow-EntireFlow img ': {
      display: 'inline-block',
    },
    '.Flow-EntireFlow ins ': {
      'text-decoration': 'underline',
    },
    '.Flow-EntireFlow kbd ': {
      'font-family': 'monospace',
    },
    '.Flow-EntireFlow label ': {
      cursor: 'default',
    },
    '.Flow-EntireFlow legend ': {
      display: 'block',
      'padding-left': '2px',
      'padding-right': '2px',
      border: 'none',
    },
    '.Flow-EntireFlow li ': {
      display: 'list-item',
    },
    '.Flow-EntireFlow link ': {
      display: 'none',
    },
    '.Flow-EntireFlow map ': {
      display: 'inline',
    },
    '.Flow-EntireFlow mark ': {
      'background-color': 'yellow',
      color: 'black',
    },
    '.Flow-EntireFlow menu ': {
      display: 'block',
      'list-style-type': 'disc',
      'margin-top': '1em',
      'margin-bottom': '1em',
      'margin-left': '0',
      'margin-right': '0',
      'padding-left': '40px',
    },
    '.Flow-EntireFlow nav ': {
      display: 'block',
    },
    '.Flow-EntireFlow object:focus ': {
      outline: 'none',
    },
    '.Flow-EntireFlow ol ': {
      display: 'block',
      'list-style-type': 'decimal',
      'margin-top': '1em',
      'margin-bottom': '1em',
      'margin-left': '0',
      'margin-right': '0',
      'padding-left': '40px',
    },
    '.Flow-EntireFlow output ': {
      display: 'inline',
    },
    '.Flow-EntireFlow p ': {
      display: 'block',
      'margin-top': '1em',
      'margin-bottom': '1em',
      'margin-left': '0',
      'margin-right': '0',
    },
    '.Flow-EntireFlow param ': {
      display: 'none',
    },
    '.Flow-EntireFlow pre ': {
      display: 'block',
      'font-family': 'monospace',
      'white-space': 'pre',
      margin: '1em 0',
    },
    '.Flow-EntireFlow q ': {
      display: 'inline',
    },
    '.Flow-EntireFlow q::before ': {
      content: 'open-quote',
    },
    '.Flow-EntireFlow q::after ': {
      content: 'close-quote',
    },
    '.Flow-EntireFlow rt ': {
      'line-height': 'normal',
    },
    '.Flow-EntireFlow s ': {
      'text-decoration': 'line-through',
    },
    '.Flow-EntireFlow samp ': {
      'font-family': 'monospace',
    },
    '.Flow-EntireFlow script ': {
      display: 'none',
    },
    '.Flow-EntireFlow section ': {
      display: 'block',
    },
    '.Flow-EntireFlow small ': {
      'font-size': 'smaller',
    },
    '.Flow-EntireFlow strike ': {
      'text-decoration': 'line-through',
    },
    '.Flow-EntireFlow strong ': {
      'font-weight': 'bold',
    },
    '.Flow-EntireFlow style ': {
      display: 'none',
    },
    '.Flow-EntireFlow sub ': {
      'vertical-align': 'sub',
      'font-size': 'smaller',
    },
    '.Flow-EntireFlow summary ': {
      display: 'block',
    },
    '.Flow-EntireFlow sup ': {
      'vertical-align': 'super',
      'font-size': 'smaller',
    },
    '.Flow-EntireFlow table ': {
      display: 'table',
      'border-collapse': 'separate',
      'border-spacing': '2px',
      'border-color': 'gray',
    },
    '.Flow-EntireFlow tbody ': {
      display: 'table-row-group',
      'vertical-align': 'middle',
      'border-color': 'inherit',
    },
    '.Flow-EntireFlow td ': {
      display: 'table-cell',
      'vertical-align': 'inherit',
    },
    '.Flow-EntireFlow tfoot ': {
      display: 'table-footer-group',
      'vertical-align': 'middle',
      'border-color': 'inherit',
    },
    '.Flow-EntireFlow th ': {
      display: 'table-cell',
      'vertical-align': 'inherit',
      'font-weight': 'bold',
      'text-align': 'center',
    },
    '.Flow-EntireFlow thead ': {
      display: 'table-header-group',
      'vertical-align': 'middle',
      'border-color': 'inherit',
    },
    '.Flow-EntireFlow title ': {
      display: 'none',
    },
    '.Flow-EntireFlow tr ': {
      display: 'table-row',
      'vertical-align': 'inherit',
      'border-color': 'inherit',
    },
    '.Flow-EntireFlow u ': {
      'text-decoration': 'underline',
    },
    '.Flow-EntireFlow ul ': {
      display: 'block',
      'list-style-type': 'disc',
      'margin-top': '1em',
      'margin-bottom': '1em',
      'margin-left': '0',
      'margin-right': '0',
      'padding-left': '40px',
    },
    '.Flow-EntireFlow var ': {
      'font-style': 'italic',
    },
  }
}

function getNormalizedStyles(builderVersion: number | undefined = 3): FlowStyles {
  const defaultStyles = {
    'font-size': 'inherit',
    'font-family': 'inherit',
    'font-weight': 'inherit',
    'text-decoration': 'inherit',
    color: 'inherit',
    transition: 'background-color 0.3s, color 0.3s',
  }
  const defaultFocus = { 'outline-color': 'var(--primary-color)' }
  const defaultDisabled = {
    cursor: 'default',
    background: '#eee',
    'pointer-events': 'none',
    color: '#aaa',
  }
  const pending = { 'pointer-events': 'none', opacity: '0.6' }
  const spinner = { display: 'inline-block' }
  const spinnerStyles = {
    'font-size': '0.9em',
    margin: '0 0.25em 0 -0.5em',
    display: 'none',
  }
  const validationFailedStyles = {
    color: '#141516',
    background: '#fd8ba0',
    'line-height': '110%',
    'border-bottom-left-radius': '4px',
    'border-bottom-right-radius': '4px',
    padding: '10px',
    'margin-top': '-1px',
    'font-weight': '400',
  }

  const styles: FlowStyles = {
    '.Flow-EntireFlow a.button.pending': pending,
    '.Flow-EntireFlow button.pending': pending,
    '.Flow-EntireFlow input.pending': pending,
    '.Flow-EntireFlow textarea.pending': pending,

    '.Flow-EntireFlow a.button.pending .icon.fa-spinner': spinner,
    '.Flow-EntireFlow button.pending .icon.fa-spinner': spinner,
    '.Flow-EntireFlow input.pending .icon.fa-spinner': spinner,
    '.Flow-EntireFlow textarea.pending .icon.fa-spinner': spinner,

    '.Flow-EntireFlow a.button .icon.fa-spinner': spinnerStyles,
    '.Flow-EntireFlow button .icon.fa-spinner': spinnerStyles,
    '.Flow-EntireFlow input .icon.fa-spinner': spinnerStyles,
    '.Flow-EntireFlow textarea .icon.fa-spinner': spinnerStyles,
  }

  if (builderVersion <= 3) {
    styles['.Flow-EntireFlow a.button'] = defaultStyles
    styles['.Flow-EntireFlow button'] = defaultStyles
    styles['.Flow-EntireFlow input'] = defaultStyles
    styles['.Flow-EntireFlow textarea'] = defaultStyles

    styles['.Flow-EntireFlow a.button:focus'] = defaultFocus
    styles['.Flow-EntireFlow button:focus'] = defaultFocus
    styles['.Flow-EntireFlow input:focus'] = defaultFocus
    styles['.Flow-EntireFlow textarea:focus'] = defaultFocus

    styles['.Flow-EntireFlow a.button[disabled]'] = defaultDisabled
    styles['.Flow-EntireFlow a.button.disabled'] = defaultDisabled
    styles['.Flow-EntireFlow button[disabled]'] = defaultDisabled
    styles['.Flow-EntireFlow button.disabled'] = defaultDisabled
    styles['.Flow-EntireFlow input[disabled]'] = defaultDisabled
    styles['.Flow-EntireFlow input.disabled'] = defaultDisabled
    styles['.Flow-EntireFlow textarea[disabled]'] = defaultDisabled
    styles['.Flow-EntireFlow textarea.disabled'] = defaultDisabled

    styles['.Flow-EntireFlow .FileUpload .validation-failed'] = validationFailedStyles
    styles['.Flow-EntireFlow .InputBox .validation-failed'] = validationFailedStyles
    styles['.Flow-EntireFlow .OptionSelector .validation-failed'] = validationFailedStyles
  }

  return styles
}

function getAnimations(flow: Flow): string {
  let animationsToAdd = []

  animationsToAdd.push(createAnimation('spin'))

  //* I add this for legacy reasons
  animationsToAdd.push(createAnimation('page-enter'))

  const transitions = flow.defaults?.transitions
  if (transitions?.enter_transition?.name) {
    const { name, duration, timing_function } = transitions?.enter_transition
    if (name !== 'no_transition')
      animationsToAdd.push(createAnimation(`page-enter-${name}`, duration, timing_function))
  }
  if (transitions?.exit_transition?.name) {
    const { name, duration, timing_function } = transitions?.exit_transition
    if (name !== 'no_transition')
      animationsToAdd.push(createAnimation(`page-exit-${name}`, duration, timing_function))
  }

  flow.pages?.forEach((page) => {
    if (page.transition?.enter_transition?.name) {
      const { name, duration, timing_function } = page.transition.enter_transition
      if (name !== 'no_transition')
        animationsToAdd.push(createAnimation(`page-enter-${name}`, duration, timing_function))
    }
    if (page.transition?.exit_transition?.name) {
      const { name, duration, timing_function } = page.transition.exit_transition
      if (name !== 'no_transition')
        animationsToAdd.push(createAnimation(`page-exit-${name}`, duration, timing_function))
    }
  })

  return animationsToAdd.join(' ')
}

function createAnimation(
  name:
    | 'page-enter'
    | 'page-enter-slide_and_fade_right_to_left'
    | 'page-exit-slide_and_fade_right_to_left'
    | 'page-enter-slide_and_fade_bottom_to_top'
    | 'page-exit-slide_and_fade_bottom_to_top'
    | 'page-enter-fade_in'
    | 'page-exit-fade_in'
    | 'page-enter-fade_out'
    | 'page-exit-fade_out'
    | 'page-enter-slide_right_to_left'
    | 'page-exit-slide_right_to_left'
    | 'page-enter-slide_bottom_to_top'
    | 'page-exit-slide_bottom_to_top'
    | 'spin',
  duration: number = 300,
  timingFunction: string = 'linear'
) {
  const animations = {
    'page-enter': `.page-enter {animation: flow-page-enter 0.3s linear;} @keyframes flow-page-enter {0% {transform: translateY(20px);opacity: 0;pointer-events: none;} 100% {opacity: 1;transform: translateY(0px);pointer-events: all;}}`,
    'page-enter-slide_and_fade_right_to_left': `.page-enter-slide_and_fade_right_to_left-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction};} @keyframes ${name} {0% {transform: translateX(40px);opacity: 0;pointer-events: none;} 100% {opacity: 1;transform: translateX(0px);pointer-events: all;}}`,
    'page-exit-slide_and_fade_right_to_left': `.page-exit-slide_and_fade_right_to_left-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction} forwards;} @keyframes ${name} {0% {transform: translateX(0px);opacity: 1;pointer-events: all;} 100% {opacity: 0;transform: translateX(-40px);pointer-events: none;}}`,
    'page-enter-slide_and_fade_bottom_to_top': `.page-enter-slide_and_fade_bottom_to_top-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction};} @keyframes ${name} {0% {transform: translateY(40px);opacity: 0;pointer-events: none;} 100% {opacity: 1;transform: translateY(0px);pointer-events: all;}}`,
    'page-exit-slide_and_fade_bottom_to_top': `.page-exit-slide_and_fade_bottom_to_top-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction} forwards;} @keyframes ${name} {0% {transform: translateY(0px);opacity: 1;pointer-events: all;} 100% {opacity: 0;transform: translateY(-40px);pointer-events: none;}}`,
    'page-enter-slide_right_to_left': `.page-enter-slide_right_to_left-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction};} @keyframes ${name} {0% {transform: translateX(100vw);pointer-events: none;} 100% {transform: translateX(0px);pointer-events: all;}}`,
    'page-exit-slide_right_to_left': `.page-exit-slide_right_to_left-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction} forwards;} @keyframes ${name} {0% {transform: translateX(0px);pointer-events: all;} 100% {transform: translateX(-100vw);pointer-events: none;}}`,
    'page-enter-slide_bottom_to_top': `.page-enter-slide_bottom_to_top-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction};} @keyframes ${name} {0% {transform: translateY(100vh);pointer-events: none;} 100% {transform: translateY(0px);pointer-events: all;}}`,
    'page-exit-slide_bottom_to_top': `.page-exit-slide_bottom_to_top-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction} forwards;} @keyframes ${name} {0% {transform: translateY(0px);pointer-events: all;} 100% {transform: translateY(-100vh);pointer-events: none;}}`,
    'page-enter-fade_in': `.page-enter-fade_in-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction};} @keyframes ${name} {0% {opacity: 0;pointer-events: none;} 100% {opacity: 1;pointer-events: all;}}`,
    'page-exit-fade_in': `.page-exit-fade_in-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction} forwards;} @keyframes ${name} {0% {opacity: 0;pointer-events: none;} 100% {opacity: 1;pointer-events: all;}}`,
    'page-enter-fade_out': `.page-enter-fade_out-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction};} @keyframes ${name} {0% {opacity: 1;pointer-events: all;} 100% {opacity: 0;pointer-events: none;}}`,
    'page-exit-fade_out': `.page-exit-fade_out-${duration}-${timingFunction} {animation: ${name} ${duration}ms ${timingFunction} forwards;} @keyframes ${name} {0% {opacity: 1;pointer-events: all;} 100% {opacity: 0;pointer-events: none;}}`,
    spin: `.spin {animation: spin 1s linear infinite;} @keyframes spin {0% {transform: rotate(0deg);} 100% {transform: rotate(360deg);}}`,
  }
  return animations[name]
}

function generatePopupStyles(): FlowStyles {
  const position = { position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }
  return {
    '.ElementType-FlowPopupOuter': {
      'z-index': 99999,
      'overflow-y': 'scroll',
      opacity: 0,
      'pointer-events': 'none',
      transition: 'opacity 0.3s',
      ...position,
    },
    '.ElementType-FlowPopupOverlay': {
      ...position,
      'background-color': 'rgba(255, 255, 255, 1)',
    },
    '.ElementType-FlowPopupOuter.popup-open': {
      opacity: 1,
      'pointer-events': 'all',
    },
    '.ElementType-FlowPopupContainer': {
      'max-width': 'unset',
    },
    '.ElementType-FlowPopupCloseButton': {
      display: 'flex',
      margin: '20px auto 0',
      'align-items': 'center',
      'background-color': 'white',
      'border-radius': '100px',
      position: 'relative',
      padding: '2px 10px',
      color: 'rgba(0, 0, 0, 0.8)',
      border: 'none',
      cursor: 'pointer',
    },
    '.ElementType-FlowPopupCloseButton:hover': {
      'background-color': 'rgba(255, 255, 255, 0.8)',
      color: 'black',
    },
    '.ElementType-FlowPopupCloseButtonIcon': {
      'margin-right': '0.25em',
    },
  }
}

function generateInfoBoxStyles(): FlowStyles {
  return {
    '.InfoBox': {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      'border-radius': 'inherit',
      'z-index': '150',
      margin: '-1px',
      overflow: 'hidden',
      padding: '1.5rem',
    },
    '.InfoBox .InfoBox-background': {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      'background-color': 'rgba(0, 0, 0, 0.3)',
      'border-radius': 'inherit',
      'z-index': 151,
      opacity: 1,
      transition: 'opacity 0.3s',
    },
    '.InfoBox .InfoBox-box': {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      'max-height': '90%',
      background: 'white',
      'border-radius': 'inherit',
      'border-top-left-radius': '16px',
      'border-top-right-radius': '16px',
      border: '1px solid #ddd',
      padding: '20px',
      'overflow-y': 'scroll',
      'z-index': 152,
      transition: 'bottom 0.3s',
    },
    //* Hide native dialog backdrop so only the styled .InfoBox-background div is visible.
    '.InfoBox dialog.InfoBox-box::backdrop': {
      display: 'none',
    },
    '.InfoBox .InfoBox-box .close': {
      position: 'absolute',
      'z-index': 10,
      right: '25px',
      top: '25px',
      color: '#aaa',
      cursor: 'pointer',
    },
    '.InfoBox .InfoBox-box .close:hover': {
      color: 'inherit',
    },
    '.InfoBox:not(.showing)': {
      'pointer-events': 'none',
    },
    '.InfoBox:not(.showing) .InfoBox-background': {
      opacity: 0,
    },
    '.InfoBox:not(.showing) .InfoBox-box': {
      bottom: '-100vh',
    },
  }
}

function generateProgressBarStyles(): FlowStyles {
  return {
    '.ProgressBar.FormProgressCircle': {
      cursor: 'pointer',
      margin: '8px',
    },
    '.ProgressBar .ElementType-BarNode.completed.navigate': {
      cursor: 'pointer',
    },
    '.ProgressBar .ElementType-BarNode.fill': {
      opacity: 0,
    },
    '.ProgressBar .ElementType-BarNode.fill.completed': {
      animation: '0.05s linear 1 normal forwards running fade-in-bar',
      'animation-delay': '0.25s',
    },
    '.ProgressBar .ElementType-BarNodeConnctor .ElementType-BarNodeConnectorInner': {
      flex: '0 0 0%',
      height: 'inherit',
      width: 'inherit',
    },
    '.ProgressBar .ElementType-BarNodeConnctor .ElementType-BarNodeConnectorInner.completed': {
      flex: '0 0 100%',
    },
    '.ProgressBar .ElementType-BarNodeConnctor .ElementType-BarNodeConnectorInner.fill': {
      transition: 'all 0.25s linear',
      'transform-origin': 'left center',
      transform: 'scaleX(0)',
    },
    '.ProgressBar .ElementType-BarNodeConnctor .ElementType-BarNodeConnectorInner.fill.completed': {
      transition: 'all 0.25s linear',
      'transform-origin': 'left center',
      transform: 'scaleX(1)',
    },
    '.FormProgress': {
      position: 'relative',
      'border-top-left-radius': '16px',
      'border-top-right-radius': '16px',
      overflow: 'hidden',
    },
    '.FormProgress:not(.FormProgressCircle)': {
      'border-bottom': '1px solid #eee',
    },
    '.FormProgress .bar': {
      background: 'var(--primary-color)',
      height: '30px',
      transition: 'width 0.3s',
    },
    '.FormProgress:hover > .progress': {
      color: 'var(--primary-color)',
    },
    '.FormProgress .progress': {
      position: 'absolute',
      top: '7px',
      left: '15px',
      'font-size': '12px',
      color: 'rgba(0, 0, 0, 0.2)',
      transition: 'color 0.3s',
    },
    '.FormProgress .progress.altColor': {
      color: 'white',
    },
    '.FormProgress .reset': {
      position: 'absolute',
      top: '9px',
      right: '14px',
      'font-size': '12px',
      color: 'rgba(0, 0, 0, 0.2)',
      transition: 'color 0.3s',
      cursor: 'pointer',
    },
    '.FormProgress .reset:hover': {
      color: 'var(--primary-color)',
    },
    '.FormProgress .reset.percent-100:hover': {
      color: 'white',
    },
    '.FormProgress.circular': {
      display: 'flex',
      'flex-direction': 'column',
      'align-items': 'center',
      overflow: 'visible',
      margin: '8px',
      border: 'none',
    },
    '.FormProgress.circular  .ep-container': {
      cursor: 'pointer',
    },
    '.FormProgress.circular.right': {
      'flex-direction': 'row',
      'justify-content': 'center',
    },
    '.FormProgress.circular.right > div': {
      'margin-right': '10px',
    },
    '.FormProgress.circular.left': {
      'flex-direction': 'row-reverse',
      'justify-content': 'center',
    },
    '.FormProgress.circular.left > div': {
      'margin-left': '10px',
    },
    '.FormProgress.circular:not(.inside) .progress-bar-legend': {
      display: 'none',
    },
  }
}

function generateOptionSelectorDropdownStyles() {
  return {
    '.select:focus': {
      'border-color': 'blue',
    },
    '.ElementType-OptionDropdown[data-multiple] div.header': {
      display: 'none',
    },
    '.ElementType-OptionDropdown .ElementType-DropdownMain': {
      '-webkit-box-align': 'center',
      '-ms-flex-align': 'center',
      'align-items': 'center',
      padding: 0,
      position: 'relative',
      flex: 1,
      display: 'flex',
      width: 'auto',
      'box-sizing': 'border-box',
      'border-style': 'solid',
      'border-width': '1px',
      'border-color': '#e8ebf0',
      'border-radius': 'inherit',
      cursor: 'pointer',
      'line-height': 1,
      transition: 'all .3s',
    },
    // '.ElementType-OptionDropdown .ElementType-DropdownMain:hover': {
    //   'background-color': '#fafafa',
    // },
    '.ElementType-OptionDropdown .ElementType-DropdownMain::after': {
      content: "'⌄'",
      'align-self': 'stretch',
      display: 'flex',
      'align-content': 'center',
      'justify-content': 'center',
      'justify-items': 'center',
      'align-items': 'center',
      padding: '0 15px 9px',
      color: '#bbb',
    },

    '.ElementType-OptionDropdown .ElementType-DropdownMain:hover::after': {
      color: '#999',
    },

    '.ElementType-OptionDropdown .ElementType-DropdownMain select': {
      '-webkit-appearance': 'none',
      '-moz-appearance': 'none',
      appearance: 'none',
      'font-family': 'inherit',
      'font-size': 'inherit',
      padding: 0,
      'border-width': 0,
      width: '100%',
      flex: 1,
      display: 'none',
    },
    '.ElementType-OptionDropdown .ElementType-DropdownMain select div.option': {
      display: 'none',
    },
    '.ElementType-OptionDropdown': {
      'user-select': 'none',
      'box-sizing': 'border-box',
      position: 'relative',
      'border-radius': '4px',
      'border-style': 'solid',
      'border-width': 0,
      'border-color': 'gray',
      width: 'auto',
      display: 'inline-block',
    },
    '.ElementType-OptionDropdown:focus': {
      'border-color': 'blue',
    },
    '.ElementType-OptionDropdown[data-open]': {
      'border-bottom-left-radius': 0,
      'border-bottom-right-radius': 0,
    },
    '.ElementType-OptionDropdown[data-open] .ElementType-DropdownList': {
      display: 'initial',
      overflow: 'scroll',
      'max-height': '250px',
    },
    '.ElementType-OptionDropdown .ElementType-DropdownList': {
      '-webkit-appearance': 'none',
      '-moz-appearance': 'none',
      appearance: 'none',
      position: 'absolute',
      'border-style': 'solid',
      'border-width': '1px',
      'border-color': '#e8ebf0',
      left: 0,
      display: 'none',
      width: '100%',
      'line-height': 1,
      'box-sizing': 'border-box',
      'z-index': 2,
      'border-bottom-left-radius': '4px',
      'border-bottom-right-radius': '4px',
    },
    '.ElementType-OptionDropdown .ElementType-DropdownList .ElementType-DropdownListOption': {
      'background-color': 'white',
      cursor: 'pointer',
      padding: '10px 14px',
      'border-width': 0,
    },
    '.ElementType-OptionDropdown .ElementType-DropdownList .ElementType-DropdownListOption:hover': {
      'background-color': '#fafafa',
    },
    '.ElementType-OptionDropdown .ElementType-DropdownList .ElementType-DropdownListOption:focus': {
      'background-color': '#fafafa',
    },
    '.ElementType-OptionDropdown .ElementType-DropdownList .ElementType-DropdownListOption:checked':
      {
        'background-color': '#f5f5f5',
      },
    // '.ElementType-OptionDropdown div.header': {
    // flex: 1,
    // display: 'flex',
    // width: 'auto',
    // 'box-sizing': 'border-box',
    // 'border-width': '1px',
    // 'border-style': 'inherit',
    // 'border-color': 'inherit',
    // 'border-radius': 'inherit',
    // },

    '.ElementType-OptionDropdown div.header span': {
      flex: 1,
      padding: '10px 14px',
    },
    '.ElementType-OptionDropdown div.header span input': {
      background: 'inherit',
      border: 'none',
      font: 'inherit',
      'font-weight': 'inherit',
      color: 'inherit',
      outline: 'none',
      width: '100%',
    },
    '.ElementType-OptionDropdown div.header span .option-selector-tag': {
      padding: '5px',
      border: '1px solid grey',
      'border-radius': '5px',
    },
  }
}

function generateSliderStyles(): FlowStyles {
  return {
    ".InputBox input[type='range']": {
      '-webkit-appearance': 'none',
      background: 'transparent',
      margin: 'var(--thumb-height, 24px)/2 0',
      width: 'var(--track-width, 100%)',
    },
    ".InputBox input[type='range']::-moz-focus-outer": {
      border: 0,
    },
    ".InputBox input[type='range']:focus": {
      outline: 0,
    },
    ".InputBox input[type='range']:focus::-ms-fill-lower": {
      background: 'var(--track-color, #eceff1)',
    },
    ".InputBox input[type='range']::-webkit-slider-runnable-track": {
      cursor: 'default',
      height: 'var(--track-height, 8px)',
      transition: 'all 0.2s ease',
      width: 'var(--track-width, 100%)',
      'box-shadow':
        'var(--track-shadow-size, 1px) var(--track-shadow-size, 1px) var(--track-shadow-blur, 1px) var(--track-shadow-color, rgba(0, 0, 0, 0.2)), 0 0 var(--track-shadow-size, 1px) var(--track-shadow-color, rgba(0, 0, 0, 0.2))',
      background: 'var(--track-color, #eceff1)',
      border: 'var(--track-border-width, 2px) solid var(--track-border-color, #cfd8dc)',
      'border-radius': 'var(--track-radius, 5px)',
    },
    ".InputBox input[type='range']::-webkit-slider-thumb": {
      'box-shadow':
        'var(--thumb-shadow-size, 4px) var(--thumb-shadow-size, 4px) var(--thumb-shadow-blur, 4px) var(--thumb-shadow-color, rgba(0, 0, 0, 0.2)), 0 0 var(--thumb-shadow-size, 4px) var(--thumb-shadow-color, rgba(0, 0, 0, 0.2))',
      background: 'var(--thumb-color, #607d8b)',
      border: 'var(--thumb-border-width, 2px) solid var(--thumb-border-color, #eceff1)',
      'border-radius': 'var(--thumb-radius, 12px)',
      'box-sizing': 'border-box',
      cursor: 'default',
      height: 'var(--thumb-height, 24px)',
      width: 'var(--thumb-width, 24px)',
      '-webkit-appearance': 'none',
      'margin-top':
        'calc((var(--track-height, 8px) - var(--track-border-width, 2px) * 2) / 2 - var(--thumb-height, 24px) / 2)',
    },
    ".InputBox input[type='range']::-moz-range-track": {
      'box-shadow':
        'var(--track-shadow-size, 1px) var(--track-shadow-size, 1px) var(--track-shadow-blur, 1px) var(--track-shadow-color, rgba(0, 0, 0, 0.2)), 0 0 var(--track-shadow-size, 1px) var(--track-shadow-color, rgba(0, 0, 0, 0.2))',
      cursor: 'default',
      // "height": "var(--track-height, 8px)",
      transition: 'all 0.2s ease',
      width: 'var(--track-width, 100%)',
      background: 'var(--track-color, #eceff1)',
      border: 'var(--track-border-width, 2px) solid var(--track-border-color, #cfd8dc)',
      'border-radius': 'var(--track-radius, 5px)',
      height: 'calc(var(--track-height, 8px) / 2)',
    },
    ".InputBox input[type='range']::-moz-range-thumb": {
      'box-shadow':
        'var(--thumb-shadow-size, 4px) var(--thumb-shadow-size, 4px) var(--thumb-shadow-blur, 4px) var(--thumb-shadow-color, rgba(0, 0, 0, 0.2)), 0 0 var(--thumb-shadow-size, 4px) var(--thumb-shadow-color, rgba(0, 0, 0, 0.2))',
      background: 'var(--thumb-color, #607d8b)',
      border: 'var(--thumb-border-width, 2px) solid var(--thumb-border-color, #eceff1)',
      'border-radius': 'var(--thumb-radius, 12px)',
      'box-sizing': 'border-box',
      cursor: 'default',
      height: 'var(--thumb-height, 24px)',
      width: 'var(--thumb-width, 24px)',
    },
    ".InputBox input[type='range']::-ms-track": {
      cursor: 'default',
      height: 'var(--track-height, 8px)',
      transition: 'all 0.2s ease',
      width: 'var(--track-width, 100%)',
      background: 'transparent',
      'border-color': 'transparent',
      'border-width': 'var(--thumb-height, 24px)/2 0',
      color: 'transparent',
    },
    ".InputBox input[type='range']::-ms-fill-lower": {
      'box-shadow':
        'var(--track-shadow-size, 1px) var(--track-shadow-size, 1px) var(--track-shadow-blur, 1px) var(--track-shadow-color, rgba(0, 0, 0, 0.2)), 0 0 var(--track-shadow-size, 1px) var(--track-shadow-color, rgba(0, 0, 0, 0.2))',
      background: 'var(--track-color, #eceff1)',
      border: 'var(--track-border-width, 2px) solid var(--track-border-color, #cfd8dc)',
      'border-radius': 'calc(var(--track-radius, 5px) * 2)',
    },

    ".InputBox input[type='range']::-ms-fill-upper": {
      'box-shadow':
        'var(--track-shadow-size, 1px) var(--track-shadow-size, 1px) var(--track-shadow-blur, 1px) var(--track-shadow-color, rgba(0, 0, 0, 0.2)), 0 0 var(--track-shadow-size, 1px) var(--track-shadow-color, rgba(0, 0, 0, 0.2))',
      background: 'var(--track-color, #eceff1)',
      border: 'var(--track-border-width, 2px) solid var(--track-border-color, #cfd8dc)',
      'border-radius': 'calc(var(--track-radius, 5px) * 2)',
    },

    ".InputBox input[type='range']::-ms-thumb": {
      'box-shadow':
        'var(--thumb-shadow-size, 4px) var(--thumb-shadow-size, 4px) var(--thumb-shadow-blur, 4px) var(--thumb-shadow-color, rgba(0, 0, 0, 0.2)), 0 0 var(--thumb-shadow-size, 4px) var(--thumb-shadow-color, rgba(0, 0, 0, 0.2))',
      background: 'var(--thumb-color, #607d8b)',
      border: 'var(--thumb-border-width, 2px) solid var(--thumb-border-color, #eceff1)',
      'border-radius': 'var(--thumb-radius, 12px)',
      'box-sizing': 'border-box',
      cursor: 'default',
      height: 'var(--thumb-height, 24px)',
      width: 'var(--thumb-width, 24px)',
      'margin-top': 'calc(var(--track-height, 8px) / 4)',
    },

    ".InputBox input[type='range']:disabled::-webkit-slider-thumb, [type='range']:disabled::-moz-range-thumb, [type='range']:disabled::-ms-thumb, [type='range']:disabled::-webkit-slider-runnable-track, [type='range']:disabled::-ms-fill-lower, [type='range']:disabled::-ms-fill-upper":
      {
        cursor: 'not-allowed',
      },
    '.InputBox div.slider': {
      display: 'flex',
      'flex-direction': 'row',
    },
    ".InputBox div.slider input[type='range']": {
      border: 'none',
    },
    ".InputBox div.slider input[type='range'].range-addon": {
      width: 'calc(25%)',
      // width: 20%,
    },
  }
}

function generateToggleSwitchStyles(): FlowStyles {
  return {
    '.ToggleSwitch': {
      display: 'flex',
      'align-items': 'center',
      cursor: 'pointer',
      margin: '10px',
    },
    '.ToggleSwitch:hover .switch .knob': {
      background: '#edf0f5',
    },
    '.ToggleSwitch.isOn .switch': {
      background: '#6454f3',
    },
    '.ToggleSwitch.isOn .switch .knob': {
      'margin-left': '14px',
    },
    '.ToggleSwitch .switch': {
      height: '15px',
      width: '30px',
      background: '#edf0f5',
      'border-radius': '8.5px',
      border: '1px solid #ccc',
      transition: 'background-color 0.3s',
    },
    '.ToggleSwitch .switch .knob': {
      margin: '-1px',
      border: '1px solid #ccc',
      height: '15px',
      width: '15px',
      background: 'white',
      'border-radius': '8.5px',
      transition: 'margin 0.3s, background-color 0.3s',
    },
    '.ToggleSwitch .label': {
      'margin-left': '10px',
      'margin-right': '10px',
    },
  }
}

function generateFileUploadStyles(): FlowStyles {
  return {
    '.FileUpload .uploader': {
      display: 'flex',
      'flex-direction': 'column',
      position: 'relative',
      width: '100%',
      height: '100%',
      'text-align': 'center',
      background: '#fafafa',
      border: '1px solid #d9d9d9',
      'border-radius': 'var(--border-radius)',
      cursor: 'pointer',
      '-webkit-transition': 'border-color 0.3s',
      transition: 'border-color 0.3s',
      '-webkit-box-sizing': 'border-box',
      'box-sizing': 'border-box',
      margin: '0',
      padding: '0',
      color: 'rgba(0, 0, 0, 0.65)',
      'font-size': '14px',
      'font-variant': 'tabular-nums',
      'line-height': '1.5',
      'list-style': 'none',
      '-webkit-font-feature-settings': "'tnum'",
      'font-feature-settings': "'tnum'",
      outline: '0',
    },
    '.FileUpload .uploader p.file-upload-text': {
      margin: '0 0 4px',
      color: 'rgba(0, 0, 0, 0.85)',
      'font-size': '16px',
    },

    '.FileUpload .uploader p.file-upload-hint': {
      color: 'rgba(0, 0, 0, 0.45)',
      'font-size': '14px',
    },

    '.FileUpload .icon': {
      'font-size': '40px',
    },
  }
}

function generateBookMeetingStyles(): FlowStyles {
  return {
    '.BookMeeting': {
      position: 'relative',
      width: '1060px',
      'max-width': '100%',
      'min-height': '680px',
      overflow: 'hidden',
    },

    '.BookMeeting.service-hubspot': {
      'margin-top': '0',
    },

    '.BookMeeting .calendly-container': {
      position: 'absolute',
      height: '680px',
      'z-index': '1',
      top: '-20px',
      bottom: '0',
      left: '-10px',
      right: '-10px',
    },

    '.BookMeeting > .spinner': {
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      right: '0',
      'z-index': '0',
      display: 'flex',
      'align-items': 'center',
      'justify-content': 'center',
    },
    '.BookMeeting > .spinner .icon': {
      'font-size': '60px',
      color: '#aaa',
    },
  }
}

function orderJsonStyles(styles: FlowStyles): FlowStyles {
  //? 1° .Flow-EntireFlow[max-width~='Xpx'] should go to the end order desc by X
  //? 2° Respect order of insertion

  //* Separate styles with and without breakpoint
  const entries = Object.entries(styles)
  const withBreakpoint = entries.filter(([key]) => key.includes('[max-width~='))
  const withoutBreakpoint = entries.filter(([key]) => !key.includes('[max-width~='))

  //* Order selectors with breakpoint from highest to lowest
  const orderedWithBreakpoint = withBreakpoint.sort((a, b) => {
    const aBreakpoint = parseInt(a[0].match(/\[max-width~='(\d+)px'\]/)?.[1] || '0', 10)
    const bBreakpoint = parseInt(b[0].match(/\[max-width~='(\d+)px'\]/)?.[1] || '0', 10)
    return bBreakpoint - aBreakpoint
  })

  //* Combine keeping the original order of selectors without breakpoint
  const finalOrder = [...withoutBreakpoint, ...orderedWithBreakpoint]

  return Object.fromEntries(finalOrder)
}
